// RUTA: /src/hooks/useBriefingManager.js
// Hook para gestionar briefings, generación de contenido y revisiones

import { useState } from 'react';
import { doc, updateDoc, getDoc, deleteDoc, collection, addDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions, auth } from '../firebase';
import { toast } from 'react-hot-toast';
import { useSchool } from '../context/SchoolContext';

// El briefing en dos pasos tarda 60–90 s; el límite por defecto de una llamada es 70 s.
// Igual al timeoutSeconds de la función (300 s).
const BRIEFING_TIMEOUT_MS = 300000;

/**
 * Hook para gestionar briefings y generación de contenido
 * @param {string} selectedStudent - ID del estudiante seleccionado
 * @returns {Object} Handlers y estados para briefings
 */
export const useBriefingManager = (selectedStudent) => {
  const school = useSchool();
  const schoolId = school?.ai?.schoolId || 'sfs';
  const [chatContent, setChatContent] = useState("");
  const [summaryContent, setSummaryContent] = useState("");
  const [classDate, setClassDate] = useState(new Date().toISOString().split('T')[0]);
  const [zoomSummary, setZoomSummary] = useState("");
  const [tutorNotes, setTutorNotes] = useState("");
  const [isCreatingBriefing, setIsCreatingBriefing] = useState(false);
  const [isCreatingClass, setIsCreatingClass] = useState(false);
  const [reviewingContent, setReviewingContent] = useState(null);
  const [reviewingSummary, setReviewingSummary] = useState(null);
  const [pendingNotification, setPendingNotification] = useState(null);
  const [isSendingNotification, setIsSendingNotification] = useState(false);

  // Crear clase (nuevo workflow)
  const handleCreateClass = async (classData) => {
    const { studentId, studentName, classDate, duration } = classData;

    if (!studentId || !studentName || !classDate || !duration) {
      toast.error("Por favor, completa todos los campos obligatorios.");
      return;
    }

    setIsCreatingClass(true);

    try {
      const createClassFunction = httpsCallable(functions, 'createClass');

      const result = await createClassFunction(classData);

      console.log('[createClass] Resultado exitoso:', result.data);
      toast.success(`¡Clase creada! ID: ${result.data.classId}`);
      return result.data.classId;
    } catch (error) {
      console.error("[createClass] Error detallado:", error);
      console.error("[createClass] Error code:", error.code);
      console.error("[createClass] Error message:", error.message);
      console.error("[createClass] Error details:", error.details);
      
      // Mostrar mensaje de error más descriptivo
      let errorMessage = "Error al crear clase";
      if (error.code === 'functions/internal') {
        errorMessage = "Error interno del servidor. Por favor, verifica los logs de Firebase Functions.";
      } else if (error.code === 'functions/unauthenticated') {
        errorMessage = "Sesión expirada. Por favor, recarga la página e inicia sesión de nuevo.";
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
      throw error;
    } finally {
      setIsCreatingClass(false);
    }
  };

  // Crear briefing desde clase (nuevo workflow)
  // Devuelve true si el briefing se creó (la ventana del historial se cierra solo entonces).
  const handleCreateBriefingFromClass = async (classId, briefingData) => {
    const { chatContent, zoomSummary, tutorNotes } = briefingData;
    
    // Solo el resumen es obligatorio; el chat y las notas suman pero no bloquean.
    if (!zoomSummary?.trim()) {
      toast.error("Falta el resumen de la clase.");
      return false;
    }
    
    setIsCreatingBriefing(true);

    try {
      // Obtener datos de la clase primero (para compatibilidad con versión antigua de la función)
      const classRef = doc(db, 'classes', classId);
      const classSnap = await getDoc(classRef);
      
      if (!classSnap.exists()) {
        toast.error("Clase no encontrada.");
        setIsCreatingBriefing(false);
        return false;
      }
      
      const classData = classSnap.data();
      const studentId = classData.studentId;
      // Use classDateLocal if available (preserves original datetime as ISO string)
      // Otherwise fall back to timestamp conversion
      const classDate = classData.classDateLocal || classData.classDate.toDate().toISOString();

      const createBriefingFunction = httpsCallable(functions, 'createSessionBriefing', { timeout: BRIEFING_TIMEOUT_MS });

      const promise = createBriefingFunction({
        classId: classId,
        studentId: studentId,
        classDate: classDate,
        chatContent: chatContent || '',
        zoomSummary: zoomSummary,
        tutorNotes: tutorNotes || '',
        schoolId,
      });

      toast.promise(promise, {
        loading: 'Analizando sesión con IA...',
        success: (result) => {
          return `¡Briefing creado! ID: ${result.data.briefingId}`;
        },
        error: (err) => `Error al crear el briefing: ${err.message}`,
      });

      await promise;
      return true;
    } catch (error) {
      console.error("Error detallado:", error);
      toast.error(`Error: ${error.message}`);
      return false;
    } finally {
      setIsCreatingBriefing(false);
    }
  };

  // Crear briefing de sesión (legacy - mantiene compatibilidad)
  const handleCreateBriefing = async () => {
    if (!selectedStudent || !classDate || !zoomSummary.trim()) {
      toast.error("Por favor, completa el estudiante, la fecha y el resumen de la clase.");
      return;
    }
    
    setIsCreatingBriefing(true);

    const createBriefingFunction = httpsCallable(functions, 'createSessionBriefing', { timeout: BRIEFING_TIMEOUT_MS });

    const promise = createBriefingFunction({
      studentId: selectedStudent,
      classDate: classDate,
      chatContent: chatContent,
      zoomSummary: zoomSummary,
      tutorNotes: tutorNotes,
      schoolId,
    });

    toast.promise(promise, {
      loading: 'Analizando sesión con IA...',
      success: (result) => {
        setChatContent("");
        setZoomSummary("");
        setTutorNotes("");
        // No reseteamos la fecha, podría ser útil para la siguiente entrada
        return `¡Briefing creado! ID: ${result.data.briefingId}`;
      },
      error: (err) => `Error al crear el briefing: ${err.message}`,
    });

    try {
      await promise;
    } catch (error) {
      console.error("Error detallado:", error);
    } finally {
      setIsCreatingBriefing(false);
    }
  };

  // Aprobar briefing (el email se encola solo si el mentor confirma en el modal).
  // La historia y la práctica ya no se esperan: el servidor las genera aparte
  // (generateApprovedBriefingContent) y la lista de clases muestra "Preparando…".
  const handleApproveBriefing = async (briefingId, sendImmediately = true) => {
    const approveFunction = httpsCallable(functions, 'approveAndSendBriefingEmail', { timeout: 180000 });

    const toastId = toast.loading('Aprobando briefing…');

    try {
      const result = await approveFunction({ briefingId, sendImmediately });

      if (result?.data?.success && result?.data?.studentId) {
        setPendingNotification({
          contentType: 'briefing',
          studentId: result.data.studentId,
          briefingId: result.data.briefingId,
          contentTitle: result.data.contentTitle,
          suggestedSendAt: null, // briefings always send immediately
          briefingPayload: result.data.payload,
        });
      }

      toast.success('Briefing aprobado. La historia y la práctica se están preparando; te aparecen para revisar en unos minutos.', { id: toastId });
    } catch (error) {
      toast.error(`Error: ${error.message}`, { id: toastId });
      console.error("Error aprobando briefing:", error);
    }
  };

  // Descartar briefing
  const handleDiscardBriefing = async (briefingId) => {
    if (!window.confirm("¿Seguro que quieres descartar este briefing? No podrás generar contenido a partir de él.")) return;

    const briefingRef = doc(db, "session_briefings", briefingId);
    try {
      await updateDoc(briefingRef, { status: 'discarded' });
      toast.success("Briefing descartado.");
    } catch (error) {
      console.error("Error discarding briefing:", error);
      toast.error("Error al descartar el briefing.");
    }
  };

  // Archivar briefing (soft delete, reversible)
  const handleArchiveBriefing = async (briefingId) => {
    if (!window.confirm("¿Archivar este briefing? Se ocultará de la bandeja activa, pero podrás restaurarlo más tarde.")) return;

    const archiveFn = httpsCallable(functions, 'archiveBriefing');
    const promise = archiveFn({ briefingId });

    toast.promise(promise, {
      loading: 'Archivando briefing...',
      success: 'Briefing archivado.',
      error: (error) => `Error al archivar: ${error.message}`,
    });

    try {
      await promise;
    } catch (error) {
      console.error('Error archiving briefing:', error);
    }
  };

  // Restaurar briefing archivado
  const handleRestoreBriefing = async (briefingId) => {
    const restoreFn = httpsCallable(functions, 'restoreBriefing');
    const promise = restoreFn({ briefingId });

    toast.promise(promise, {
      loading: 'Restaurando briefing...',
      success: 'Briefing restaurado.',
      error: (error) => `Error al restaurar: ${error.message}`,
    });

    try {
      await promise;
    } catch (error) {
      console.error('Error restoring briefing:', error);
    }
  };

  // Eliminar briefing permanentemente (solo si ya está archivado y sin contenido aprobado)
  const handleDeleteBriefingPermanently = async (briefingId) => {
    if (!window.confirm("¿Eliminar este briefing permanentemente? Esta acción no se puede deshacer.")) return;

    const deleteFn = httpsCallable(functions, 'deleteBriefingPermanently');
    try {
      await deleteFn({ briefingId });
      toast.success('Briefing eliminado permanentemente.');
    } catch (error) {
      console.error('Error deleting briefing permanently:', error);
      toast.error(error.message || 'Error al eliminar el briefing.');
    }
  };

  // Generar práctica individual (flashcard, matching, etc.)
  const handleGeneratePractice = async (briefingId, exerciseType) => {
    const generatePracticeFn = httpsCallable(functions, 'generatePracticeSet');
    const promise = generatePracticeFn({ briefingId, exerciseType });

    const typeLabels = {
      'flashcard': 'Flashcards',
      'matching_pairs': 'Pares de Emparejamiento',
      'fill_in_the_blank': 'Rellenar Espacios',
      'traduccion': 'Traducción'
    };

    toast.promise(promise, {
      loading: `Generando ${typeLabels[exerciseType] || exerciseType}...`,
      success: `¡${typeLabels[exerciseType] || exerciseType} generados!`,
      error: 'Error al generar la práctica.',
    });
  };

  // Generar práctica completa (todos los tipos)
  const handleGenerateCompletePractice = async (briefingId) => {
    // Timeout explícito en ms — debe cubrir el timeoutSeconds:540 del backend
    // (functions/src/content/practiceSet.js). Sin esto, el SDK cliente corta a
    // los 70s por defecto: con 5 tipos de ejercicio (incluyendo diálogo y
    // dictado) la generación real puede tardar más que eso, y el cliente
    // reporta error/cuelgue aunque el servidor termine bien segundos después.
    const generateCompletePracticeFn = httpsCallable(functions, 'generateCompletePracticeSet', { timeout: 540000 });
    const promise = generateCompletePracticeFn({ briefingId, schoolId });

    toast.promise(promise, {
      loading: 'Generando práctica completa (4 tipos + resumen)...',
      success: '¡Práctica completa generada! Todo listo para revisión.',
      error: 'Error al generar la práctica completa.',
    });
  };

  // Generar historia
  const handleGenerateStory = async (briefingId) => {
    const generateStoryFn = httpsCallable(functions, 'generateStory');
    const promise = generateStoryFn({ briefingId, schoolId });

    toast.promise(promise, {
      loading: 'Generando historia y actividades...',
      success: '¡Historia generada!',
      error: 'Error al generar la historia.',
    });
  };

  // Guardar cambios en briefing
  const handleSaveChangesBriefing = async (briefingId, newBriefingData) => {
    const briefingRef = doc(db, "session_briefings", briefingId);
    try {
      // newBriefingData contiene { tutorAnalysis, studentSummary } y, desde la confirmación
      // con tramas (entrega 3), storyPlotChoice: la trama elegida para la historia.
      await updateDoc(briefingRef, {
        tutorAnalysis: newBriefingData.tutorAnalysis,
        studentSummary: newBriefingData.studentSummary,
        ...(Number.isInteger(newBriefingData.storyPlotChoice) && { storyPlotChoice: newBriefingData.storyPlotChoice }),
      });
      toast.success("Briefing actualizado.");
      return true;
    } catch (error) {
      console.error("Error saving briefing changes:", error);
      toast.error("Error al guardar cambios en el briefing.");
      return false;
    }
  };

  // Revisar contenido generado (práctica, historia, etc.)
  const handleReviewContent = async (contentId, type) => {
    try {
      const contentRef = doc(db, "pending_reviews", contentId);
      const contentSnap = await getDoc(contentRef);
      if (contentSnap.exists()) {
        const contentData = { id: contentSnap.id, type, ...contentSnap.data() };
        setReviewingContent(contentData);
      } else {
        toast.error("El contenido a revisar no fue encontrado.");
      }
    } catch (error) {
      console.error("Error loading review content:", error);
      toast.error("Error al cargar el contenido para revisión.");
    }
  };

  // Aprobar y liberar contenido al estudiante (FASE 2.1)
  const handleApproveAndRelease = async (contentData) => {
    const approveFunction = httpsCallable(functions, 'approveAndReleaseContent');

    // Payload simplificado: solo reviewId y contentData editado
    const payload = {
      reviewId: contentData.id,
      contentData: contentData.contentData
    };

    const promise = approveFunction(payload);

    // Mensaje diferente según el tipo de contenido
    const isStory = contentData.type === 'story';
    toast.promise(promise, {
      loading: isStory
        ? 'Aprobando historia y generando actividades...'
        : 'Aprobando y liberando contenido...',
      success: isStory
        ? '¡Historia y actividades liberadas!'
        : '¡Contenido liberado al estudiante!',
      error: (err) => `Error al liberar: ${err.message}`,
    });

    try {
      const result = await promise;
      setReviewingContent(null);
      // Show notification confirmation modal with data from the function response
      if (result?.data?.success && result?.data?.studentId) {
        setPendingNotification({
          contentType: result.data.type,
          studentId: result.data.studentId,
          contentId: result.data.contentId,
          contentTitle: result.data.contentTitle,
          contentImageUrl: result.data.contentImageUrl || null,
          suggestedSendAt: result.data.suggestedSendAt,
          // P4: el aviso guarda su briefing, para que la cola escriba ahí si salió.
          briefingId: contentData?.sourceBriefingId || null,
        });
      }
    } catch (error) {
      console.error("Error aprobando contenido:", error);
    }
  };

  // Colombia = UTC-5 fijo (sin DST).
  // Todas las funciones usan Date.UTC() para evitar dependencia de la zona local del dispositivo.

  // Calcula Timestamp UTC para "mañana a las `hour`:00 en Colombia"
  function getColombiaNextDayAtHour(hour) {
    // Hora actual en Colombia = UTC - 5h
    const nowUtcMs = Date.now();
    const colombiaOffsetMs = 5 * 60 * 60 * 1000; // 5h en ms
    const nowColombiaMs = nowUtcMs - colombiaOffsetMs;
    // Medianoche hoy en Colombia (en ms desde epoch, expresado como UTC)
    const midnightColombiaUtcMs = Math.floor(nowColombiaMs / 86400000) * 86400000 + colombiaOffsetMs;
    // Mañana a las `hour`:00 Colombia → sumar 1 día + hour horas, todo en UTC
    const targetUtcMs = midnightColombiaUtcMs + 86400000 + hour * 3600000;
    return Timestamp.fromDate(new Date(targetUtcMs));
  }

  // Calcula Timestamp UTC a partir de un string datetime-local (e.g. "2026-04-10T10:30")
  // interpretado como hora Colombia (UTC-5). Usa Date.UTC para evitar ambigüedad de zona local.
  function getColombiaCustomTimestamp(isoString) {
    // Parsear manualmente para evitar que el navegador interprete la hora como local
    const [datePart, timePart] = isoString.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hh, mm] = (timePart || '00:00').split(':').map(Number);
    // Colombia = UTC-5: hora Colombia + 5h = UTC
    const utcMs = Date.UTC(year, month - 1, day, hh + 5, mm, 0, 0);
    return Timestamp.fromDate(new Date(utcMs));
  }

  // Encolar notificación desde el frontend (después de confirmación del mentor)
  const handleConfirmNotification = async (send, scheduleInfo) => {
    if (!pendingNotification) return;
    if (!send) {
      // Decidir no enviar es válido (ningún correo sale solo). Se guarda en el
      // briefing para que el historial muestre "No enviado" y no parezca un
      // aviso olvidado — pedido de Vic, 2026-10-06 (P5 de admin-mentor-integracion.md).
      const { contentType, briefingId } = pendingNotification;
      const type = contentType === 'briefing' ? 'briefing_ready' : contentType === 'practice' ? 'practice_ready' : 'story_ready';
      setPendingNotification(null);
      if (briefingId) {
        try {
          await updateDoc(doc(db, 'session_briefings', briefingId), {
            [`notifications.${type}`]: { status: 'skipped', at: serverTimestamp() },
          });
        } catch (mirrorError) {
          console.warn('[useBriefingManager] No se pudo guardar "no enviado" en el briefing:', mirrorError);
        }
      }
      return;
    }

    const { contentType, studentId, contentId, contentTitle, contentImageUrl, briefingId, briefingPayload } = pendingNotification;

    setIsSendingNotification(true);
    try {
      // Calcular sendAt según la opción elegida en el modal
      const option = scheduleInfo?.option || 'now';
      let sendAt;
      if (option === 'tomorrow_9') {
        sendAt = getColombiaNextDayAtHour(9);
      } else if (option === 'tomorrow_18') {
        sendAt = getColombiaNextDayAtHour(18);
      } else if (option === 'custom' && scheduleInfo?.customDatetime) {
        sendAt = getColombiaCustomTimestamp(scheduleInfo.customDatetime);
      } else {
        sendAt = Timestamp.now();
      }

      let notifData;
      if (contentType === 'briefing') {
        notifData = {
          type: 'briefing_ready',
          studentId,
          briefingId,
          contentTitle: contentTitle || '',
          status: 'pending',
          createdAt: serverTimestamp(),
          sendAt,
          payload: briefingPayload || {},
        };
      } else {
        notifData = {
          type: contentType === 'practice' ? 'practice_ready' : 'story_ready',
          studentId,
          contentId,
          contentTitle: contentTitle || '',
          status: 'pending',
          createdAt: serverTimestamp(),
          sendAt,
        };
        if (contentType === 'story' && contentImageUrl) {
          notifData.contentImageUrl = contentImageUrl;
        }
        if (briefingId) notifData.briefingId = briefingId;
      }

      const notifRef = await addDoc(collection(db, 'scheduled_notifications'), notifData);
      // P4 (admin-mentor-integracion.md): el historial muestra "Programado" con la
      // hora; la cola después lo cambia a enviado o falló. No crítico.
      if (briefingId) {
        try {
          await updateDoc(doc(db, 'session_briefings', briefingId), {
            [`notifications.${notifData.type}`]: { status: 'scheduled', notificationId: notifRef.id, sendAt },
          });
        } catch (mirrorError) {
          console.warn('[useBriefingManager] No se pudo marcar el aviso en el briefing:', mirrorError);
        }
      }
      toast.success('Notificación programada correctamente.');
    } catch (error) {
      console.error('Error queueing notification:', error);
      toast.error('Error al programar la notificación.');
    } finally {
      setIsSendingNotification(false);
      setPendingNotification(null);
    }
  };

  // Reintentar un aviso al estudiante que falló — P4 de admin-mentor-integracion.md.
  // El id del aviso viene del briefing (notifications.<tipo>.notificationId): la
  // cola es solo-admin para leer, y firestore.rules solo deja al mentor del
  // estudiante pasar ese aviso de failed a pending. Sale en la próxima vuelta
  // de la cola (cada 15 min).
  const handleRetryNotice = async (briefing, noticeType) => {
    const entry = briefing?.notifications?.[noticeType];
    if (!entry?.notificationId || entry.status !== 'failed') {
      toast.error('Este aviso no se puede reintentar desde aquí.');
      return;
    }
    const sendAt = Timestamp.now();
    try {
      await updateDoc(doc(db, 'scheduled_notifications', entry.notificationId), {
        status: 'pending',
        sendAt,
        retriedAt: serverTimestamp(),
        retriedBy: auth?.currentUser?.uid || null,
      });
      await updateDoc(doc(db, 'session_briefings', briefing.id), {
        [`notifications.${noticeType}`]: { status: 'scheduled', notificationId: entry.notificationId, sendAt },
      });
      toast.success('Aviso reprogramado: sale en los próximos minutos.');
    } catch (error) {
      console.error('[useBriefingManager] Error reintentando el aviso:', error);
      toast.error('No se pudo reintentar el aviso.');
    }
  };

  // Descartar contenido en revisión (FASE 2.1)
  const handleDiscardContent = async (reviewId) => {
    if (!window.confirm("¿Seguro que quieres descartar este contenido? Esta acción no se puede deshacer.")) return;

    const discardFunction = httpsCallable(functions, 'discardPendingReview');
    const promise = discardFunction({ reviewId });

    toast.promise(promise, {
      loading: 'Descartando contenido...',
      success: '¡Contenido descartado!',
      error: 'Error al descartar el contenido.',
    });

    try {
      await promise;
      setReviewingContent(null);
    } catch (error) {
      console.error("Error descartando contenido:", error);
    }
  };

  // Actualizar historia ya aprobada en personalized_content
  // El doc guarda storyData y activitiesData en el root (no bajo contentData)
  const handleUpdateApprovedStory = async (storyId, contentData) => {
    try {
      const updates = {};
      if (contentData.storyData) updates.storyData = contentData.storyData;
      if (contentData.activitiesData) updates.activitiesData = contentData.activitiesData;
      if (contentData.storyData?.titulo) updates.title = contentData.storyData.titulo;
      await updateDoc(doc(db, 'personalized_content', storyId), updates);
      toast.success('Historia actualizada.');
    } catch (error) {
      console.error('Error updating approved story:', error);
      toast.error('Error al actualizar la historia.');
    }
  };

  // Actualizar set de práctica ya aprobado en practice_sets
  // El doc guarda exercisesByType en el root (no bajo contentData)
  const handleUpdateApprovedPracticeSet = async (practiceSetId, contentData) => {
    try {
      const updates = {};
      if (contentData.exercisesByType) updates.exercisesByType = contentData.exercisesByType;
      await updateDoc(doc(db, 'practice_sets', practiceSetId), updates);
      toast.success('Set de práctica actualizado.');
    } catch (error) {
      console.error('Error updating approved practice set:', error);
      toast.error('Error al actualizar el set de práctica.');
    }
  };

  // Eliminar historia aprobada de personalized_content
  const handleDeleteApprovedStory = async (storyId, briefingId) => {
    try {
      await deleteDoc(doc(db, 'personalized_content', storyId));
      await updateDoc(doc(db, 'session_briefings', briefingId), { storyId: null });
      toast.success('Historia eliminada.');
    } catch (error) {
      console.error('Error deleting approved story:', error);
      toast.error('Error al eliminar la historia.');
    }
  };

  // Eliminar set de práctica aprobado de practice_sets
  const handleDeleteApprovedPracticeSet = async (practiceSetId, briefingId) => {
    try {
      await deleteDoc(doc(db, 'practice_sets', practiceSetId));
      await updateDoc(doc(db, 'session_briefings', briefingId), { practiceSetId: null });
      toast.success('Set de práctica eliminado.');
    } catch (error) {
      console.error('Error deleting approved practice set:', error);
      toast.error('Error al eliminar el set de práctica.');
    }
  };

  // Guardar cambios en contenido en revisión (FASE 2.1)
  const handleSaveChangesContent = async (payload) => {
    // payload es { reviewId, newContentData }
    const saveFunction = httpsCallable(functions, 'updatePendingReviewContent');
    const promise = saveFunction(payload);

    toast.promise(promise, {
      loading: 'Guardando cambios...',
      success: '¡Cambios guardados exitosamente!',
      error: 'Error al guardar los cambios.',
    });

    try {
      await promise;
      // Recargar el contenido actualizado
      await handleReviewContent(payload.reviewId, null); // El tipo no importa aquí, ya viene en el documento
    } catch (error) {
      console.error("Error guardando cambios:", error);
    }
  };

  // Aprobar historia fase 1: genera actividades + busca imagen Pexels
  // Guarda resultado en pending_reviews (status: phase1_complete), NO publica aún
  const handleApproveStoryPhase1 = async (contentData) => {
    // Timeout explícito en ms — cubre el timeoutSeconds:300 del backend
    // (functions/src/content/workflow.js, approveStoryPhase1) con margen. Sin esto, el
    // SDK cliente corta a los 70s por defecto: la cadena Gemini→(retry)→Claude fallback→
    // Pexels puede superar eso aunque el backend termine bien segundos/minutos después.
    const phase1Function = httpsCallable(functions, 'approveStoryPhase1', { timeout: 540000 });
    const payload = {
      reviewId: contentData.id,
      contentData: contentData.contentData
    };

    const promise = phase1Function(payload);
    toast.promise(promise, {
      loading: 'Generando actividades y buscando imagen...',
      success: '¡Actividades generadas! Revísalas antes de publicar.',
      error: (err) => `Error: ${err.message}`,
    });

    try {
      const result = await promise;
      if (result?.data?.success) {
        // Reload updated pending_review (now has activitiesData + imageUrl + phase1_complete)
        await handleReviewContent(contentData.id, 'story');
        // Success toast above already fired via toast.promise — a partial/degraded
        // generation still returns success:true, so surface it as a separate warning
        // instead of letting the silent gap through (item 5: never swallow a partial rescue).
        if (result.data.activitiesWarning) {
          toast(result.data.activitiesWarning, { icon: '⚠️', duration: 8000 });
        }
      }
      return result?.data;
    } catch (error) {
      console.error("Error en fase 1 de aprobación:", error);
      return null;
    }
  };

  // Publicar historia (fase 2): crea personalized_content y limpia pending_reviews
  const handlePublishStory = async (contentData) => {
    const publishFunction = httpsCallable(functions, 'publishStory');
    const payload = {
      reviewId: contentData.id,
      contentData: contentData.contentData
    };

    const promise = publishFunction(payload);
    toast.promise(promise, {
      loading: 'Publicando historia...',
      success: '¡Historia publicada!',
      error: (err) => `Error al publicar: ${err.message}`,
    });

    try {
      const result = await promise;
      setReviewingContent(null);
      if (result?.data?.success && result?.data?.studentId) {
        setPendingNotification({
          contentType: result.data.type,
          studentId: result.data.studentId,
          contentId: result.data.contentId,
          contentTitle: result.data.contentTitle,
          contentImageUrl: result.data.contentImageUrl || null,
          suggestedSendAt: result.data.suggestedSendAt,
          // P4: el aviso guarda su briefing, para que la cola escriba ahí si salió.
          briefingId: contentData?.sourceBriefingId || null,
        });
      }
    } catch (error) {
      console.error("Error publicando historia:", error);
    }
  };

  // Regenerar historia con una plantilla de trama específica
  const handleRegenerateStoryWithTemplate = async (briefingId, templateId, refinementInstructions) => {
    const generateFunction = httpsCallable(functions, 'generateStory');
    const promise = generateFunction({ briefingId, templateId, refinementInstructions: refinementInstructions || '', schoolId });

    toast.promise(promise, {
      loading: 'Regenerando historia con nueva trama...',
      success: '¡Historia regenerada! Revisa el nuevo borrador.',
      error: (err) => `Error al regenerar: ${err.message}`,
    });

    try {
      const result = await promise;
      if (result?.data?.reviewId) {
        await handleReviewContent(result.data.reviewId, 'story');
      }
      return result?.data;
    } catch (error) {
      console.error("Error regenerando historia:", error);
      return null;
    }
  };

  // Generar traducciones para las palabras seleccionadas por el mentor
  // Llama a la Cloud Function que usa el prompt vocabularyEnrichment
  const handleGenerateVocabularyTranslations = async (words) => {
    const enrichFunction = httpsCallable(functions, 'enrichVocabulary');
    const promise = enrichFunction({ words, schoolId });

    toast.promise(promise, {
      loading: 'Generando traducciones...',
      success: '¡Traducciones generadas!',
      error: (err) => `Error: ${err.message}`,
    });

    try {
      const result = await promise;
      return result?.data?.enrichedWords || [];
    } catch (error) {
      console.error("Error generando traducciones:", error);
      return [];
    }
  };

  // Enviar email de resumen de clase
  const handleSendClassEmail = async (briefingId) => {
    const sendEmailFunction = httpsCallable(functions, 'approveAndSendBriefingEmail', { timeout: 540000 });
    const promise = sendEmailFunction({ briefingId, sendImmediately: true });

    toast.promise(promise, {
      loading: 'Enviando email de resumen...',
      success: '¡Email enviado correctamente!',
      error: (err) => `Error al enviar email: ${err.message}`,
    });

    try {
      await promise;
    } catch (error) {
      console.error("Error enviando email:", error);
    }
  };

  return {
    // Estados
    chatContent,
    setChatContent,
    summaryContent,
    setSummaryContent,
    classDate,
    setClassDate,
    zoomSummary,
    setZoomSummary,
    tutorNotes,
    setTutorNotes,
    isCreatingBriefing,
    isCreatingClass,
    reviewingContent,
    setReviewingContent,
    pendingNotification,
    isSendingNotification,

    // Handlers de clases (nuevo workflow)
    handleCreateClass,
    handleCreateBriefingFromClass,

    // Handlers de briefings
    handleCreateBriefing,
    handleApproveBriefing,
    handleDiscardBriefing,
    handleSaveChangesBriefing,
    handleArchiveBriefing,
    handleRestoreBriefing,
    handleRetryNotice,
    handleDeleteBriefingPermanently,

    // Handlers de generación
    handleGeneratePractice,
    handleGenerateCompletePractice,
    handleGenerateStory,

    // Handlers de revisión de contenido
    handleReviewContent,
    handleApproveAndRelease,
    handleDiscardContent,
    handleSaveChangesContent,

    // Handlers nuevo flujo de historias (2 pasos)
    handleApproveStoryPhase1,
    handlePublishStory,
    handleRegenerateStoryWithTemplate,
    handleGenerateVocabularyTranslations,

    // Handler de notificación post-aprobación
    handleConfirmNotification,

    // Handler de email
    handleSendClassEmail,

    // Handlers de edición/eliminación de contenido aprobado
    handleUpdateApprovedStory,
    handleUpdateApprovedPracticeSet,
    handleDeleteApprovedStory,
    handleDeleteApprovedPracticeSet,
  };
};
